import { useEffect, useState } from 'react'
import { Col, Container, Row } from 'react-grid-system'
import { CreateOwnerForm } from '../organism'
import styled from 'styled-components'
import { BackButton, LoadingSpinner } from '../atoms'
import { useLocation, useNavigate } from 'react-router'
import {
  useErrorHandlerContext,
  useOpeningElementsControllerContext,
  useUserLanguage
} from '@/customHooks'
import { OwnerDetail } from '@/custom-types'
import { CreateOwnerMutation, UpdateOwnerMutation, useGetOwnerDetailLazyQuery } from '@/graphql/generated'
import { FetchResult } from '@apollo/client'

const textsData = {
  en: {
    title: 'Owner',
    create: 'Add',
    edit: 'Edit',
    detail: 'Deatil',
    confirmation: {
      create: {
        title: 'Owner has been created',
        description: (firstName: string, lastName: string) => `User ${firstName} ${lastName} has been created succesfully`,
      },
      update: {
        title: 'Owner has been updated',
        description: (firstName: string, lastName: string) => `User ${firstName} ${lastName} has been updated succesfully`,
      }
    }
  },
  es: {
    title: 'Propietario',
    create: 'Agregar',
    edit: 'Editar',
    detail: ', detalles',
    confirmation: {
      create: {
        title: 'Propietario creado con éxito',
        description: (firstName: string, lastName: string) => `${firstName} ${lastName} fue creado con éxito`,
      },
      update: {
        title: 'Propietario actualizado con exito',
        description: (firstName: string, lastName: string) => `${firstName} ${lastName} fue actualizado con éxito`,
      }
    }
  }
}

const StyledContainer = styled(Container)`
  padding-top: ${({ theme }) => theme.spacing.lg};
  padding-bottom: ${({ theme }) => theme.spacing.lg};
`

const FormContainer = styled(Col)`
  & > form {
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);
    border-radius: ${({ theme }) => theme.border.radius.base};
  }
`

export const CreateOwnerPage = () => {
  const userLanguage = useUserLanguage()
  const texts = userLanguage === 'es' ? textsData.es : textsData.en

  const { confirmDialogModalOpen } = useOpeningElementsControllerContext()

  const [currentOwner, setCurrentOwner] = useState<OwnerDetail | null>(null)

  const [titleText, setTitleText] = useState(`${texts.create} ${texts.title}`)
  
  const navigate = useNavigate()

  const location = useLocation()

  const { open: errorHandle } = useErrorHandlerContext()

  const [getOwner, {loading}] = useGetOwnerDetailLazyQuery()

  useEffect(() => {
    if (location.state) {
      if (location.state.readOnly) setTitleText(`${texts.title} ${texts.detail}`)
      else setTitleText(`${texts.edit} ${texts.title}`)
      getOwner({
        variables: {
          id: location.state.ownerId
        }
      })
      .then((response) => setCurrentOwner(response.data?.owner || null))
      .catch(errorHandle)
    }
  }, [location.state?.ownerId, getOwner])

  const onCreateSuccess = (response: FetchResult<CreateOwnerMutation> | FetchResult<UpdateOwnerMutation>) => {
      if (response.data && 'createOwner' in response.data) {
        const user = response.data?.createOwner
        if (user.__typename === 'OwnerOutput') {
          confirmDialogModalOpen({
            title: texts.confirmation.create.title,
            description: texts.confirmation.create.description(user.firstName, user.lastName),
          })
          navigate(-1)
        }
      }
      else if (response.data && 'updateOwner' in response.data) {
        const user = response.data?.updateOwner
        confirmDialogModalOpen({
          title: texts.confirmation.update.title,
          description: texts.confirmation.update.description(user.firstName, user.lastName)
        })
        navigate(-1)
      } 
    }

  return (
    <StyledContainer>
      {
        loading
          ? (
            <Row>
              <Col>
                <LoadingSpinner />
              </Col>
            </Row>
          )
          : (
            <>
              <Row align='center'>
                <Col xs="content">
                  <BackButton />
                </Col>
                <Col>
                  <h2>{titleText}</h2>
                </Col>
              </Row>
              <Row>
                <FormContainer>
                  <CreateOwnerForm
                    ownerDetail={currentOwner}
                    activity={location.state ? 'updating' : 'creating'}
                    readOnly={!!location.state?.readOnly}
                    onSuccess={onCreateSuccess}
                  />
                </FormContainer>
              </Row>
            </>
          )
      }
    </StyledContainer>
  )
}